import React from 'react';
import { motion } from 'framer-motion';
import { Star, ShoppingCart, Heart, ChevronRight, Gem } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CartSidebar } from '../components/CartSidebar';
import { CartItem, Page } from '../App';
const fashionProducts = [{
  id: 'f1',
  name: 'Luxury Leather Tote',
  price: 45000,
  originalPrice: 55000,
  image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&q=80&w=800',
  rating: 4.8,
  badge: 'Best Seller',
  weight: 0.8,
  sku: 'FASH-LLT-001',
  description: 'Premium full-grain leather tote with gold hardware'
}, {
  id: 'f2',
  name: 'Gold Chain Necklace',
  price: 28500,
  image: 'https://images.unsplash.com/photo-1599643478518-17488fbbcd75?auto=format&fit=crop&q=80&w=800',
  rating: 4.9,
  weight: 0.1,
  sku: 'FASH-GCN-002',
  description: '18K gold-plated chain necklace, 45cm length'
}, {
  id: 'f3',
  name: 'Ankara Print Dress',
  price: 18000,
  image: 'https://images.unsplash.com/photo-1585487000160-6ebcfceb0d03?auto=format&fit=crop&q=80&w=800',
  rating: 4.7,
  badge: 'New Arrival',
  weight: 0.5,
  sku: 'FASH-APD-003',
  description: 'Vibrant Ankara print midi dress, sizes S-XL'
}, {
  id: 'f4',
  name: 'Pearl Drop Earrings',
  price: 18000,
  image: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&q=80&w=800',
  rating: 4.6,
  weight: 0.05,
  sku: 'FASH-PDE-004',
  description: 'Freshwater pearl drop earrings with sterling silver'
}, {
  id: 'f5',
  name: 'Beaded Waist Beads',
  price: 8500,
  image: 'https://images.unsplash.com/photo-1611085583191-a3b181a88401?auto=format&fit=crop&q=80&w=800',
  rating: 4.8,
  weight: 0.1,
  sku: 'FASH-BWB-005',
  description: 'Handcrafted Nigerian waist beads, adjustable'
}, {
  id: 'f6',
  name: 'Designer Sunglasses',
  price: 15000,
  image: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?auto=format&fit=crop&q=80&w=800',
  rating: 4.7,
  weight: 0.2,
  sku: 'FASH-DSG-006',
  description: 'UV400 polarized designer frames'
}, {
  id: 'f7',
  name: 'Silk Evening Scarf',
  price: 12000,
  image: 'https://images.unsplash.com/photo-1601924994987-69e26d50dc26?auto=format&fit=crop&q=80&w=800',
  rating: 4.5,
  weight: 0.15,
  sku: 'FASH-SES-007',
  description: '100% pure silk scarf, 90x90cm'
}, {
  id: 'f8',
  name: 'Ankara Tote Bag',
  price: 22000,
  image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?auto=format&fit=crop&q=80&w=800',
  rating: 4.6,
  badge: 'Popular',
  weight: 0.6,
  sku: 'FASH-ATB-008',
  description: 'Handmade Ankara fabric tote with leather handles'
}];
interface PageProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
  cartItems: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
export function FashionAccessoriesPage({
  onAddToCart,
  onNavigate,
  cartItems,
  cartCount,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onUpdateQuantity,
  onRemoveItem
}: PageProps) {
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(amount);
  return <div className="bg-[#0F0F0F] min-h-screen text-white" data-id="element-480">
      <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-481" />

      {/* Header */}
      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-482">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-id="element-483">
          <button onClick={() => onNavigate('home')} className="hover:text-[#D4AF37] transition-colors" data-id="element-484">
            Home
          </button>
          <ChevronRight className="w-4 h-4" data-id="element-485" />
          <span className="text-[#D4AF37]" data-id="element-486">Fashion Accessories</span>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-4 mb-4" data-id="element-487">
          <div className="p-3 bg-[#D4AF37]/10 rounded-xl" data-id="element-488">
            <Gem className="w-7 h-7 text-[#D4AF37]" data-id="element-489" />
          </div>
          <div data-id="element-490">
            <h1 className="text-4xl md:text-5xl font-bold font-serif text-white" data-id="element-491">
              Fashion Accessories
            </h1>
            <p className="text-gray-400 mt-1" data-id="element-492">
              Curated pieces that elevate your everyday style
            </p>
          </div>
        </motion.div>
      </div>

      {/* Products Grid */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24" data-id="element-493">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-494">
          {fashionProducts.map((product, index) => <motion.div key={product.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.07
        }} className="glass-card rounded-xl overflow-hidden group flex flex-col" data-id="element-495">
              <div className="relative h-56 overflow-hidden" data-id="element-496">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-497" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" data-id="element-498" />
                {product.badge && <span className="absolute top-3 left-3 bg-[#D4AF37] text-black text-xs font-bold px-2 py-1 rounded" data-id="element-499">
                    {product.badge}
                  </span>}
                <button className="absolute top-3 right-3 p-2 bg-white/10 backdrop-blur-sm text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hover:text-black" data-id="element-500">
                  <Heart className="w-4 h-4" data-id="element-501" />
                </button>
              </div>
              <div className="p-4 flex-1 flex flex-col" data-id="element-502">
                <p className="text-xs text-gray-500 mb-1" data-id="element-503">SKU: {product.sku}</p>
                <h3 className="text-white font-medium mb-1 line-clamp-1" data-id="element-504">
                  {product.name}
                </h3>
                <p className="text-gray-500 text-xs mb-3 line-clamp-2" data-id="element-505">
                  {product.description}
                </p>
                <div className="flex items-center gap-1 mb-3" data-id="element-506">
                  <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" data-id="element-507" />
                  <span className="text-xs text-gray-400" data-id="element-508">
                    {product.rating}
                  </span>
                  <span className="text-xs text-gray-600 ml-1" data-id="element-509">
                    · {product.weight}kg
                  </span>
                </div>
                <div className="mt-auto flex items-center justify-between" data-id="element-510">
                  <div data-id="element-511">
                    <span className="text-lg font-bold text-white" data-id="element-512">
                      {formatCurrency(product.price)}
                    </span>
                    {product.originalPrice && <p className="text-xs text-gray-500 line-through" data-id="element-513">
                        {formatCurrency(product.originalPrice)}
                      </p>}
                  </div>
                  <button onClick={() => onAddToCart(product)} className="p-3 bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white rounded-lg transition-colors" data-id="element-514">
                    <ShoppingCart className="w-4 h-4" data-id="element-515" />
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      <Footer onNavigate={onNavigate} data-id="element-516" />
      <CartSidebar isOpen={isCartOpen} onClose={onCloseCart} items={cartItems} onUpdateQuantity={onUpdateQuantity} onRemoveItem={onRemoveItem} onNavigate={onNavigate} data-id="element-517" />
    </div>;
}